import { BaseFilters } from "@/shared/types";

export type CollectionStatusTab = "success" | "error" | "in_progress";

export type CollectionResultStatus = "success" | "error";

// ==================== 수집 현황 API 응답 타입 ====================
export interface CollectionSuccessRow {
  requested_time: string; // KST 포맷된 문자열 "YYYY-MM-DD HH:MM:SS"
  trans_id: string;
  channel: string;
  dw_account_id: string;
  collect_data_type: string;
  collect_type: string;
  target_date: string; // "YYYY-MM-DD" 또는 "YYYY-MM-DD ~ YYYY-MM-DD"
}

export interface CollectionErrorRow {
  requested_time: string;
  trans_id: string;
  channel: string;
  dw_account_id: string;
  collect_data_type: string;
  collect_type: string;
  target_date: string;
  err_code: string | null;
  err_msg: string | null;
}

export interface CollectionInProgressRow {
  trans_id: string;
  channel: string | null;
  dw_account_id: string | null;
  collect_data_type: string | null;
  target_date: string; // "YYYY-MM-DD ~ YYYY-MM-DD"
  phase: string | null; // e.g. "collector", "transformer", "canceling"
  job_start_time: string | null;
  retry_info: string; // "retry_count / max_retry"
}

export interface CollectionStatusSummary {
  success_count: number;
  error_count: number;
  in_progress_count: number; // 진행중 + pending
}

export interface CollectionStatusResponse {
  summary: CollectionStatusSummary;
  success_rows: CollectionSuccessRow[];
  error_rows: CollectionErrorRow[];
  in_progress_rows: CollectionInProgressRow[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CollectionStatusParams {
  tab: CollectionStatusTab;
  page: number;
  page_size: number;
  sort_by?: string;
  sort_desc?: boolean;
  keyword?: string;
  trans_id?: string;
  channel?: string;
}

// ==================== 수집 취소 API ====================
export interface CancelCollectionRequest {
  trans_ids: string[];
  requester?: string;
}

export interface CancelCollectionResponse {
  success: boolean;
  message: string;
  cancelled_count: number;
}

// ==================== Legacy 타입 (기존 페이지 호환용) ====================
export interface CollectionStatusErrorRow extends CollectionSuccessRow {
  errCode: string;
  errMsg: string;
}

export interface CollectionInProgressFilters extends BaseFilters {
  transId?: string;
  channel?: string;
}

export interface CollectionsStatusData {
  summary: CollectionStatusSummary;
  successRows: CollectionSuccessRow[];
  errorRows: CollectionStatusErrorRow[];
  inProgressRows: CollectionInProgressRow[];
}

// ==================== 수집 오류 API 타입 ====================
export type CollectionsErrorsDateRange = "today" | "past_1_day" | "past_2_days" | "past_3_days" | "past_7_days";

export const COLLECTION_ERRORS_DATE_RANGE_LABELS: Record<CollectionsErrorsDateRange, string> = {
  today: "Today",
  past_1_day: "Past 1 day",
  past_2_days: "Past 2 days",
  past_3_days: "Past 3 days",
  past_7_days: "Past 7 days",
};

export const COLLECTION_ERRORS_DATE_RANGES: CollectionsErrorsDateRange[] = [
  "today",
  "past_1_day",
  "past_2_days",
  "past_3_days",
  "past_7_days",
];

export interface CollectionErrorRowAPI {
  requested_time: string;
  trans_id: string;
  datadog_url: string;
  channel: string | null;
  sub_channel: string | null;
  dw_account_id: string;
  err_msg: string | null;
  org_name: string | null;
  workspace_ids: string[];
}

export interface CollectionKnownErrorGroupRow {
  count: number;
  channel: string;
  pattern: string;
  pattern_key: string; // "channel:pattern"
  description: string | null;
}

export interface CollectionErrorsResponse {
  new_errors: CollectionErrorRowAPI[];
  known_errors_by_pattern: Record<string, CollectionErrorRowAPI[]>;
  known_error_groups: CollectionKnownErrorGroupRow[];
}

// ==================== 수집 데이터 크기 API 타입 ====================
export interface CollectionsSizeRow {
  channel: string;
  report_type: string;
  dw_account_id: string;
  target_date: string; // YYYY-MM-DD
  row_count: number;
  file_size_mb: number;
  requested_time: string | null; // ISO string
}

export interface CollectionsSizeSummary {
  total_count: number;
  total_data_rows: number;
  total_data_size_mb: number;
}

export interface CollectionsSizeSearchParams {
  start_date: string; // YYYY-MM-DD (required)
  end_date: string; // YYYY-MM-DD (required)
  dw_account_id?: string;
  channel?: string;
  report_type?: string;
  page?: number;
  page_size?: number;
  order_by?: string;
  is_desc?: boolean;
}

export interface CollectionSizeResponse {
  summary: CollectionsSizeSummary;
  rows: CollectionsSizeRow[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==================== E2E 테스트 대시보드 API 타입 ====================
export interface E2ETestHistoryRow {
  trans_id: string;
  executed_at: string; // KST 포맷된 문자열 "YYYY-MM-DD HH:MM:SS"
  result_text: string; // "성공" | "실패" | "성공 (파일 0개)"
  processed_file_count: number;
  error_msg: string | null;
}

export interface CollectionsE2EAssetRow {
  status_circle: string; // "loading" | "🔴" | "🟠" | "🟢" | "⚪"
  channel: string;
  asset_name: string;
  asset_status: string; // "ON" | "OFF"
  dw_account_id: string;
  last_executed_at: string | null; // KST 포맷 또는 ISO string
  recent_circles_text: string; // "🟢 🟢 🟠 ⚪ ⚪"
  error_msg_preview: string | null;
  fail_desc_preview: string | null;
  can_run_test: boolean;
  is_in_progress: boolean;
  full_error_msg?: string | null;
  fail_desc?: string | null;
  media_account_id?: string | null;
}

export interface E2ETestDashboardResponse {
  assets: CollectionsE2EAssetRow[];
  history_by_dw_account_id: Record<string, E2ETestHistoryRow[]>;
}

export interface RunE2ETestAssetInfo {
  dw_account_id: string;
  media_account_id: string;
  can_run_test: boolean;
}

export interface RunE2ETestRequest {
  assets: RunE2ETestAssetInfo[];
}

export interface RunE2ETestResponse {
  success_count: number;
  error_count: number;
  error_messages: string[];
}

// Legacy type for UI (frontend에서 임시로 사용)
export interface CollectionsE2EData {
  rows: CollectionsE2EAssetRow[];
  historyByDwAccountId: Record<string, E2ETestHistoryRow[]>;
}

// ==================== Error Pattern Types ====================
export interface ErrorPatternRow {
  id: number;
  channel: string;
  pattern: string;
  description: string | null;
}

export interface ErrorPatternsResponse {
  patterns: ErrorPatternRow[];
}

export interface CreateErrorPatternRequest {
  channel: string;
  pattern: string;
  description?: string | null;
}

export interface UpdateErrorPatternRequest {
  channel: string;
  pattern: string;
  description?: string | null;
}

export interface DeleteErrorPatternsRequest {
  ids: number[];
}

export interface ErrorPatternResponse {
  pattern: ErrorPatternRow;
}

export interface DeleteErrorPatternsResponse {
  deleted_count: number;
  deleted_ids: number[];
}

// ==================== Collection Error Pattern Types ====================
export interface CollectionErrorPattern {
  id: number;
  channel: string;
  pattern: string;
  created_at: string;
  updated_at: string;
}

export type CollectionErrorPatternChannel = string;

export interface CreateCollectionErrorPatternDto {
  channel: string;
  pattern: string;
}

export interface UpdateCollectionErrorPatternDto {
  id: number;
  channel?: string;
  pattern?: string;
}
